import {isAllOf} from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';

import {showNewNotification} from '@edna/components';
import {TCurrency} from '@edna/utils';

import {TItemId} from 'src/constants';
import {TInvoice} from 'src/containers/Invoices/definitions';
import {EWebNotificationType, notificationsEndpoints} from 'src/containers/Notifications';
import {
  CACHE_LIFETIME,
  ECacheTag,
  isErrorWithCode,
  isMatchQueryMeta,
  listenerMiddleware,
  rootApi,
  showErrorNotification,
} from 'src/models';

import {
  EAutopaymentStatus,
  TAutopaymentFormData,
  TAutopaymentRestrictions,
  TGeneratePaymentLinkPayload,
  TGeneratePaymentLinkResponse,
  TPayKeeperInfo,
  TPayKeeperOrder,
  TStripeAutopayment,
  defaultStripeInvoiceParams,
} from './definitions';

const api = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // Invoice
    generatePaymentLink: builder.mutation<
      TGeneratePaymentLinkResponse,
      TGeneratePaymentLinkPayload
    >({
      query: ({tenantId, ...data}) => ({
        method: 'POST',
        url: tenantId
          ? '/billing/document/support/bill/generate'
          : '/billing/document/bill/generate',
        params: {tenantId},
        data,
        meta: {isShowError: true},
      }),
    }),
    // Autopayment
    stripeRedirect: builder.query<{stripeUrl: string}, void>({
      query: () => ({
        method: 'GET',
        url: '/stripe/checkout/customer-portal',
      }),
    }),
    getStripeAutopaymentInfo: builder.query<TStripeAutopayment | undefined, TItemId | void>({
      query: (supportTenantId) => ({
        method: 'GET',
        url:
          supportTenantId !== undefined
            ? `/stripe/checkout/support/${supportTenantId}/customer-info`
            : '/stripe/checkout/customer-info',
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.AUTOPAYMENT],
      transformResponse: (data: TStripeAutopayment) => {
        if (isEmpty(data) || data?.customerStatus === EAutopaymentStatus.NEW) {
          return undefined;
        }

        return data;
      },
    }),
    getStripeAutopaymentRestrictions: builder.query<TAutopaymentRestrictions, void>({
      query: () => ({
        method: 'GET',
        url: '/stripe/checkout/restrictions',
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    connectStripeAutopayment: builder.mutation<TStripeAutopayment, TAutopaymentFormData>({
      query: (data) => ({
        method: 'POST',
        url: '/stripe/checkout/setup-customer',
        data,
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    updateStripeAutopaymentSettings: builder.mutation<TStripeAutopayment, TAutopaymentFormData>({
      query: (data) => ({
        method: 'PUT',
        url: '/stripe/checkout/update-customer',
        data,
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    offStripeAutopayment: builder.mutation<void, TItemId | void>({
      query: (supportTenantId) => ({
        method: supportTenantId !== undefined ? 'PUT' : 'GET',
        url:
          supportTenantId !== undefined
            ? `/stripe/checkout/support/${supportTenantId}/disable-customer`
            : '/stripe/checkout/request-deactivation',
        meta: {isShowError: true},
      }),
      invalidatesTags: (_, __, supportTenantId) => (supportTenantId ? [ECacheTag.AUTOPAYMENT] : []),
    }),
    chargeStripeAutopayment: builder.mutation<void, TItemId>({
      query: (supportTenantId) => ({
        method: 'POST',
        url: `/stripe/checkout/support/${supportTenantId}/initiate-auto-replenishment`,
        meta: {isShowError: true},
      }),
      invalidatesTags: (_, __, supportTenantId) => (supportTenantId ? [ECacheTag.AUTOPAYMENT] : []),
    }),
    restoreStripeAutopayment: builder.mutation<void, TItemId | void>({
      query: (supportTenantId) => ({
        method: 'POST',
        url: supportTenantId
          ? `/stripe/checkout/support/${supportTenantId}/enable-customer`
          : '/stripe/checkout/enable-customer',
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    // OnetimePayment
    getPayKeeperInfo: builder.query<TPayKeeperInfo | void, void>({
      query: () => ({
        method: 'GET',
        url: '/paykeeper/client',
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.ONETIME_PAYMENT],
      // TODO: https://jira.edna.ru/browse/EP-6280 fix responses on backend
      transformResponse: (data: TPayKeeperInfo | string) =>
        !(data instanceof Object) || isEmpty(data) ? undefined : data,
    }),
    getTenantPayKeeperInfo: builder.query<TPayKeeperInfo[] | void, TItemId>({
      query: (id) => ({
        method: 'GET',
        url: '/paykeeper/support/client/list',
        params: {tenantId: id},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.ONETIME_PAYMENT],
      transformResponse: (data: TPayKeeperInfo[]) =>
        !(data instanceof Object) || isEmpty(data) ? undefined : data,
    }),
    createPayKeeperOrder: builder.mutation<TPayKeeperOrder, {sum: number; createBinding?: boolean}>(
      {
        query: (data) => ({
          method: 'POST',
          url: '/paykeeper/orders',
          params: data,
          meta: {isShowError: true},
        }),
        invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
      },
    ),
    payWithPayKeeper: builder.mutation<void, number>({
      query: (sum) => ({
        method: 'POST',
        url: '/paykeeper/initiate-payment',
        params: {sum},
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    deletePayKeeperInfo: builder.mutation<void, {tenantId: TItemId; userId: number} | void>({
      query: (data) => ({
        method: 'DELETE',
        url: data ? '/paykeeper/support/client' : '/paykeeper/client',
        meta: {isShowError: true},
        params: data,
      }),
      invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    createStripeInvoice: builder.mutation<TInvoice, {amount: number; currency?: TCurrency}>({
      query: (data) => ({
        method: 'POST',
        url: `/invoice/${defaultStripeInvoiceParams.kind}/create`,
        data: {...data, ...defaultStripeInvoiceParams},
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.createStripeInvoice.matchFulfilled,
  effect: (data) => {
    if (!data.payload?.paymentUrl) {
      showNewNotification({
        type: 'ERROR',
        messageKey: 'UserCompanyProfile:serviceUnavailable',
      });
    }
  },
});

listenerMiddleware.startListening({
  matcher: isAllOf(
    api.endpoints.createStripeInvoice.matchRejected,
    isMatchQueryMeta({isBackendError: true}),
  ),
  effect: (data: TAnyObject) => {
    if (isErrorWithCode(data.payload)) {
      showErrorNotification(data.payload);
    } else {
      showNewNotification({
        type: 'ERROR',
        messageKey: 'UserCompanyProfile:serviceUnavailable',
      });
    }
  },
});

listenerMiddleware.startListening({
  matcher: notificationsEndpoints.pollNotifications.matchFulfilled,
  effect: (data, listenerApi) => {
    const hasAutopaymentNotification = data.payload.webNotifications.some(
      (item) => item.type === EWebNotificationType.StripeCheckout,
    );

    if (hasAutopaymentNotification) {
      listenerApi.dispatch(api.util.invalidateTags([ECacheTag.AUTOPAYMENT]));
    }

    const hasOnetimePaymentNotification = data.payload.webNotifications.some(
      (item) => item.type === EWebNotificationType.PaykeeperPayment,
    );

    if (hasOnetimePaymentNotification) {
      listenerApi.dispatch(api.util.invalidateTags([ECacheTag.ONETIME_PAYMENT]));
    }
  },
});

export const {
  // Invoice
  useGeneratePaymentLinkMutation,
  // Autopayment
  useLazyStripeRedirectQuery,
  useGetStripeAutopaymentInfoQuery,
  useGetStripeAutopaymentRestrictionsQuery,
  useOffStripeAutopaymentMutation,
  useConnectStripeAutopaymentMutation,
  useChargeStripeAutopaymentMutation,
  useUpdateStripeAutopaymentSettingsMutation,
  useRestoreStripeAutopaymentMutation,
  // OnetimePayment
  useGetPayKeeperInfoQuery,
  useGetTenantPayKeeperInfoQuery,
  useCreatePayKeeperOrderMutation,
  useDeletePayKeeperInfoMutation,
  usePayWithPayKeeperMutation,
  useCreateStripeInvoiceMutation,
} = api;
